import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { StorageSchemaBanner } from "@/components/layout/StorageSchemaBanner";
import { PendingTasksBanner } from "@/components/layout/PendingTasksBanner";
import { WebhookEventsBanner } from "@/components/layout/WebhookEventsBanner";
import { LicenseExpiredBanner } from "@/components/layout/LicenseExpiredBanner";
import { Toaster } from "@/components/ui/toaster";
import { useLicenseStatus } from "@/hooks/use-license";
import { DevPlanSwitcher } from "@/components/layout/DevPlanSwitcher";
import { OiaWidgetHost } from "./OiaWidgetHost";
import { HelpFloatingButton } from "@/components/ui/HelpFloatingButton";
import { OnboardingModal } from "@/components/ui/OnboardingModal";

// ═══════════════════════════════════════════════════════════
// BlockedGuard — Redirects blocked accounts to billing tab.
// Runs on every route change; cannot be bypassed by URL.
// ═══════════════════════════════════════════════════════════

function BlockedGuard() {
  const { data: license } = useLicenseStatus();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (license?.status === "BLOCKED" && !location.pathname.startsWith("/settings")) {
      navigate("/settings?tab=license", { replace: true });
    }
  }, [license?.status, location.pathname, navigate]);

  return null;
}

// ═══════════════════════════════════════════════════════════
// AppShell — Layout: sidebar + main content (TPP §5.1)
// ═══════════════════════════════════════════════════════════

export function AppShell() {
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <BlockedGuard />
        <LicenseExpiredBanner />
        <StorageSchemaBanner />
        <PendingTasksBanner />
        <WebhookEventsBanner />
        <div className="w-full p-6">
          <Outlet />
        </div>
      </main>
      <Toaster />
      <OnboardingModal />
      <HelpFloatingButton />
      <OiaWidgetHost />
      {import.meta.env.DEV && <DevPlanSwitcher />}
    </div>
  );
}
